<template>
  <div>
    <div class="appui">
      <!-- LISTA -->
      <div v-if="view === 'list'">
        <div class="appui-head">
          <div>
            <h1 class="appui-title">Palestrantes</h1>
            <div class="appui-crumb">App <span class="sep">/</span> Palestrantes</div>
          </div>
        </div>

        <!-- Vazio: nenhum cadastrado (bloco próprio) -->
        <div v-if="!list.length" class="appui-emptystate">
          <div class="appui-emptystate-icon"><i class="bx bx-microphone"></i></div>
          <div class="appui-emptystate-title">Nenhum palestrante ainda</div>
          <div class="appui-emptystate-sub">Cadastre quem vai palestrar no evento — eles aparecem na agenda e no app do participante.</div>
          <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Adicionar o primeiro palestrante</button>
        </div>

        <!-- Bloco principal: controles + lista, tudo dentro do card -->
        <section v-else class="appui-card appui-card--flush">
          <div class="appui-card-toolbar">
            <div class="appui-card-toolbar-left">
              <div class="appui-search">
                <i class="bx bx-search-alt"></i>
                <input v-model.trim="search" placeholder="Buscar por nome ou cargo...">
                <button v-if="search" class="appui-search-clear" @click="search = ''" title="Limpar busca"><i class="mdi mdi-close"></i></button>
              </div>
            </div>
            <div class="appui-card-toolbar-right">
              <div class="appui-seg">
                <button :class="{ 'is-active': viewMode === 'table' }" @click="viewMode = 'table'">Tabela</button>
                <button :class="{ 'is-active': viewMode === 'cards' }" @click="viewMode = 'cards'">Cards</button>
              </div>
              <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Novo palestrante</button>
            </div>
          </div>

          <!-- Sem resultado de busca -->
          <div v-if="!filteredList.length" class="appui-inline-empty">
            Nenhum palestrante encontrado para os filtros. <a @click="clearFilters()">Limpar filtros</a>
          </div>

          <!-- Tabela (reordenação por arrastar) -->
          <table v-else-if="viewMode === 'table'" class="appui-table">
            <thead>
              <tr>
                <th style="width:56px"></th>
                <th>Palestrante</th>
                <th>Cargo / Empresa</th>
                <th>Status</th>
                <th style="text-align:right">Ações</th>
              </tr>
            </thead>
            <draggable
              v-model="displayList"
              :element="'tbody'"
              :options="{ animation: 180, handle: '.appui-grip', ghostClass: 'appui-drag-ghost', disabled: isFiltering }"
              @end="onReorder"
            >
              <tr v-for="s in displayList" :key="s.id">
                <td>
                  <span class="appui-grip" :class="{ 'is-disabled': isFiltering }" :title="isFiltering ? 'Limpe a busca e o filtro para reordenar' : 'Arraste para reordenar'">⠿</span>
                </td>
                <td>
                  <div style="display:flex;align-items:center;gap:11px">
                    <span class="appui-avatar" style="width:38px;height:38px;font-size:12px">
                      <img v-if="s.photo" :src="s.photo"><template v-else>{{ initials(s.name) }}</template>
                    </span>
                    <span style="font-weight:500;color:#495057">{{ s.name }}</span>
                  </div>
                </td>
                <td style="color:#74788d">{{ s.title || '—' }}</td>
                <td><span class="appui-badge" :class="s.status ? 'appui-badge--green' : 'appui-badge--muted'" role="button" @click="toggle(s)">{{ s.status ? 'Ativo' : 'Oculto' }}</span></td>
                <td style="text-align:right;white-space:nowrap">
                  <button class="appui-tbtn" style="margin-right:6px" @click="openEdit(s)">Editar</button>
                  <button class="appui-tbtn appui-tbtn--danger" @click="remove(s)">Excluir</button>
                </td>
              </tr>
            </draggable>
          </table>

          <!-- Cards -->
          <div v-else class="appui-grid-3" style="padding:16px">
            <div v-for="s in displayList" :key="s.id" class="appui-spk-card">
              <span class="appui-badge" :class="s.status ? 'appui-badge--green' : 'appui-badge--muted'" style="position:absolute;top:12px;right:12px">{{ s.status ? 'Ativo' : 'Oculto' }}</span>
              <div style="display:flex;gap:14px;align-items:center;margin-bottom:14px">
                <span class="appui-avatar" style="width:60px;height:60px;font-size:17px">
                  <img v-if="s.photo" :src="s.photo"><template v-else>{{ initials(s.name) }}</template>
                </span>
                <div>
                  <div style="font-weight:600;font-size:13.5px;color:#495057">{{ s.name }}</div>
                  <div style="font-size:12px;color:#74788d;margin-top:3px">{{ s.title || '—' }}</div>
                </div>
              </div>
              <div style="display:flex;gap:8px">
                <button class="appui-tbtn" @click="openEdit(s)">Editar</button>
                <button class="appui-tbtn appui-tbtn--danger" @click="remove(s)">Excluir</button>
              </div>
            </div>
          </div>

          <!-- Rodapé: mostrar X por vez + contador -->
          <div v-if="filteredList.length" class="appui-card-footer">
            <div class="appui-perpage">
              <span>Mostrar</span>
              <select class="appui-select appui-select--sm" v-model.number="perPage">
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="0">Todos</option>
              </select>
              <span>por vez</span>
            </div>
            <span class="appui-count">Mostrando {{ displayList.length }} de {{ filteredList.length }}</span>
          </div>
        </section>
      </div>

      <!-- FORM (página) -->
      <div v-else>
        <div class="appui-head">
          <div>
            <h1 class="appui-title">{{ form.id ? 'Editar palestrante' : 'Novo palestrante' }}</h1>
            <div class="appui-crumb">App <span class="sep">/</span> Palestrantes <span class="sep">/</span> {{ form.id ? form.name : 'Novo' }}</div>
          </div>
          <button class="appui-btn appui-btn--ghost" @click="backToList()"><i class="mdi mdi-arrow-left mr-1"></i> Voltar</button>
        </div>

        <section class="appui-card" style="padding:24px">
          <div style="display:flex;gap:18px;align-items:center;margin-bottom:22px;padding-bottom:20px;border-bottom:1px solid #eff2f7">
            <span class="appui-avatar" style="width:82px;height:82px;font-size:20px">
              <img v-if="preview" :src="preview"><template v-else>{{ initials(form.name) }}</template>
            </span>
            <div>
              <input type="file" accept="image/*" ref="photo" style="display:none" @change="onPhoto">
              <div style="display:flex;gap:8px">
                <button class="appui-btn appui-btn--ghost" @click="$refs.photo.click()">{{ preview ? 'Trocar foto' : 'Enviar foto' }}</button>
                <button v-if="preview" class="appui-btn appui-btn--ghost" style="color:#f46a6a" @click="removePhoto()">Remover</button>
              </div>
              <div style="font-size:11px;color:#adb5bd;margin-top:8px">JPG ou PNG quadrado · até 2 MB</div>
            </div>
          </div>

          <div class="appui-grid-2" style="margin-bottom:16px">
            <div><label class="appui-label">Nome</label><input class="appui-input" v-model="form.name"></div>
            <div><label class="appui-label">Cargo / Empresa</label><input class="appui-input" v-model="form.title"></div>
          </div>
          <div style="margin-bottom:20px"><label class="appui-label">Mini-bio</label><textarea class="appui-input" style="min-height:84px" v-model="form.description"></textarea></div>

          <h3 class="appui-sec-title" style="margin-bottom:12px">Redes sociais</h3>
          <div class="appui-grid-2" style="gap:12px 16px;margin-bottom:20px">
            <div v-for="soc in socials" :key="soc.key" style="display:flex;align-items:center;gap:10px;border:1px solid #eff2f7;border-radius:8px;padding:2px 4px 2px 12px">
              <span class="appui-icon-chip" style="width:26px;height:26px;font-size:14px;background:#f3f4f6;color:#74788d"><i class="mdi" :class="soc.icon"></i></span>
              <input v-model="form[soc.key]" :placeholder="soc.label" style="flex:1;border:none;outline:none;font-family:inherit;font-size:13px;color:#495057;background:transparent;padding:8px 6px">
            </div>
          </div>

          <div style="display:flex;justify-content:space-between;align-items:center;border-top:1px solid #eff2f7;padding-top:18px">
            <div style="display:flex;align-items:center;gap:10px">
              <span style="font-size:13px;color:#495057;font-weight:500">Ativo</span>
              <button class="appui-switch" :class="{ 'is-on': form.status }" @click="form.status = form.status ? 0 : 1"><span class="knob"></span></button>
            </div>
            <div style="display:flex;gap:10px">
              <button class="appui-btn appui-btn--ghost" @click="backToList()">Cancelar</button>
              <button class="appui-btn appui-btn--primary" :disabled="saving" @click="save()">Salvar palestrante</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, name: '', title: '', description: '', facebook: '', instagram: '', linkedin: '', twitter: '', youtube: '', website: '', status: 1 })

export default {
  components: { draggable },
  name: 'app-speakers',
  data () {
    return {
      list: [],
      view: 'list',
      viewMode: 'table',
      search: '',
      perPage: 50,
      saving: false,
      form: empty(),
      photoFile: null,
      preview: null,
      socials: [
        { key: 'website', label: 'Website', icon: 'mdi-web' },
        { key: 'linkedin', label: 'LinkedIn', icon: 'mdi-linkedin' },
        { key: 'instagram', label: 'Instagram', icon: 'mdi-instagram' },
        { key: 'facebook', label: 'Facebook', icon: 'mdi-facebook' },
        { key: 'youtube', label: 'YouTube', icon: 'mdi-youtube' },
        { key: 'twitter', label: 'Twitter / X', icon: 'mdi-twitter' }
      ]
    }
  },
  mounted () { this.load() },
  computed: {
    isFiltering () { return !!this.search },
    filteredList () {
      if (!this.search) return this.list
      const q = this.search.toLowerCase()
      return this.list.filter(s => (s.name || '').toLowerCase().includes(q) || (s.title || '').toLowerCase().includes(q))
    },
    // Itens exibidos: recorta o "mostrar X por vez". Settable para o v-model do
    // draggable — ao arrastar, remonta a ordem completa preservando o que ficou
    // fora do recorte. Reordenar só é permitido sem filtro (isFiltering=false).
    displayList: {
      get () {
        const lim = this.perPage || this.filteredList.length
        return this.filteredList.slice(0, lim)
      },
      set (val) {
        if (this.isFiltering) return
        const rest = this.list.slice(val.length)
        this.list = [...val, ...rest]
      }
    }
  },
  methods: {
    initials (name) { return (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('') },
    clearFilters () { this.search = '' },
    removePhoto () { this.photoFile = null; this.preview = null; this.form.photo = null },
    load () {
      const loader = this.$loading.show()
      services.listSpeakers().then(res => { this.list = res.data.data }).catch(() => errorMessage('Falha ao carregar palestrantes.', 3000)).finally(() => loader.hide())
    },
    openCreate () { this.form = empty(); this.photoFile = null; this.preview = null; this.view = 'form' },
    openEdit (s) { this.form = { ...empty(), ...s }; this.photoFile = null; this.preview = s.photo || null; this.view = 'form' },
    backToList () { this.view = 'list' },
    onPhoto (e) {
      const f = e.target.files[0]
      if (!f) return
      this.photoFile = f
      this.preview = URL.createObjectURL(f)
    },
    async save () {
      this.saving = true
      try {
        const fd = new FormData()
        Object.keys(empty()).forEach(k => { if (k !== 'id' && this.form[k] != null) fd.append(k, this.form[k]) })
        if (this.photoFile) fd.append('photo', this.photoFile)
        if (this.form.id) await services.updateSpeaker(this.form.id, fd)
        else await services.createSpeaker(fd)
        this.view = 'list'
        successMessage('Palestrante salvo.', 2000)
        this.load()
      } catch (e) { errorMessage('Falha ao salvar palestrante.', 3000) } finally { this.saving = false }
    },
    async toggle (s) { try { await services.changeSpeakerStatus(s.id); s.status = s.status ? 0 : 1 } catch (e) { errorMessage('Falha ao alterar status.', 3000) } },
    async remove (s) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover "${s.name}"?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteSpeaker(s.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    },
    async onReorder () {
      // A ordem já foi remontada no setter de displayList; aqui só persiste.
      try {
        await services.reorderSpeakers(this.list.map(it => ({ id: it.id })))
      } catch (e) { errorMessage('Falha ao reordenar.', 3000); this.load() }
    }
  }
}
</script>
